import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Video } from 'lucide-react';

import { CreatePostForm } from '../../widgets/post-create-form';
import { ReelCreateForm } from '../../widgets/reel-create-form';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { isDraftDirty, usePublishDraft } from '../../features/publish-groups';

export type PublicationFormat = 'reel' | 'post';

const FORMATS: { id: PublicationFormat; label: string; hint: string; icon: typeof Video }[] = [
  { id: 'reel', label: 'Reels', hint: 'Вертикальное видео 9:16', icon: Video },
  { id: 'post', label: 'Пост', hint: 'Фото или карусель до 10 файлов', icon: FileText },
];

/** Формат берём из адреса — тогда кнопка «Опубликовать» в шапке знает, какую форму отправлять. */
export function usePublicationFormat(): PublicationFormat {
  const [params] = useSearchParams();
  return params.get('format') === 'post' ? 'post' : 'reel';
}

/**
 * Единый экран создания. Раньше это были две страницы — /reels и /posts/create,
 * и выбор формата жил в выпадающем меню шапки.
 *
 * Переключатель отдаём внутрь формы, в её левую колонку: если оставить его над
 * двухколоночной сеткой, превью справа стартует ниже экрана и при прокрутке
 * успевает проехать вверх, прежде чем прилипнуть.
 */
export function PublicationNewPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const format = usePublicationFormat();

  // Черновик (галочки, описания) общий для форматов — живёт, пока открыт экран.
  useEffect(() => () => usePublishDraft.getState().reset(), []);

  // Уход со страницы с файлом или текстом — браузер переспросит.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDraftDirty(usePublishDraft.getState())) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const select = async (next: string) => {
    if (next === format) return;
    // Аккаунты и описания переедут в другой формат, а файл — нет: у рилса
    // видео, у поста фото. Терять загруженное молча нельзя.
    if (usePublishDraft.getState().hasMedia) {
      const ok = await confirmDialog({
        title: next === 'post' ? 'Перейти к посту?' : 'Перейти к рилсу?',
        description: 'Загруженный файл не переносится в другой формат. Отмеченные аккаунты и описание останутся.',
        confirmText: 'Перейти',
      });
      if (!ok) return;
    }
    const updated = new URLSearchParams(params);
    updated.set('format', next);
    setParams(updated, { replace: true });
  };

  // Чем формат отличается от формата, видно по самой форме и превью; подпись
  // под переключателем это только повторяла, поэтому осталась подсказкой.
  const formatSwitch = (
    <Tabs value={format} onValueChange={(v) => void select(v)}>
      <TabsList className="grid w-full grid-cols-2">
        {FORMATS.map(({ id, label, hint, icon: Icon }) => (
          <TabsTrigger key={id} value={id} title={hint}>
            <Icon />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );

  // key — чтобы форма пересобиралась при смене формата и не тащила чужое
  // состояние; общее (аккаунты, описания) живёт в usePublishDraft.
  return format === 'reel' ? (
    <ReelCreateForm key="reel" headerSlot={formatSwitch} />
  ) : (
    <CreatePostForm key="post" headerSlot={formatSwitch} />
  );
}
